// In-page signals from SAM to a mounted (or just-mounting) card, for commands that target one
// engagement's Workflow card without threading a prop through every workspace layer
// (e.g. EXPAND_ENGAGEMENT_JOB_CARD). The last signal per kind is kept briefly so a card that
// mounts right after it — SAM opens the job and sends the command in one envelope — still gets it.
export type EngagementSignalKind =
  | 'expand_job_card' | 'download_job_card' | 'open_job_card_section' | 'close_job_card_section' | 'open_invoice'
  | 'open_site_board' | 'refresh_site_board';

interface EngagementSignal { kind: EngagementSignalKind; engagementId: string; detail?: string; at: number }

const STALE_MS = 10_000;
const listeners = new Set<(s: EngagementSignal) => void>();
const last = new Map<EngagementSignalKind, EngagementSignal>();

export function emitEngagementSignal(kind: EngagementSignalKind, engagementId: string, detail?: string): void {
  const s = { kind, engagementId, detail, at: Date.now() };
  last.set(kind, s);
  listeners.forEach((fn) => fn(s));
}

/** Calls `onSignal` for signals of `kind` aimed at `engagementId`, including one sent just before
 *  this card mounted. Each signal is delivered once per subscriber. */
export function subscribeEngagementSignal(
  kind: EngagementSignalKind, engagementId: string, onSignal: (detail?: string) => void,
): () => void {
  const recent = last.get(kind);
  if (recent && recent.engagementId === engagementId && Date.now() - recent.at < STALE_MS) {
    last.delete(kind);
    queueMicrotask(() => onSignal(recent.detail));
  }
  const fn = (s: EngagementSignal) => {
    if (s.kind === kind && s.engagementId === engagementId) {
      last.delete(kind);
      onSignal(s.detail);
    }
  };
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
